import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../components/Spin";
import { Label } from "../../../components/ui/label";
import CustomLabelText from "../../../components/CustomLabelText";
import { Input } from "../../../components/ui/input";
import { API, faqTypeOptions, fileUpload, get } from "../../../config";
import { Button } from "../../../components/ui/button";
import Editor from "../../../components/Editor";
import SearchSelect from "../../../components/SearchSelect";
import { useAllModules } from "../../Auth/SignUpHelper";
import { Loader2 } from "lucide-react";
// import { AddAdminFaqSchema } from "../../../lib/Validation";

const AddEditFaq = ({ id, toggleShowAddEditFaq, edit, fetchFaqData }) => {
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(false);

    const { allModuleLoading, allModule = [] } = useAllModules(true);
    const module = allModule?.map((item) => {
      const { id: value, name, short_name, ...rest } = item;
      return { label: name, value, ...rest };
    });

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.GET_ADMIN_FAQ_LIST}/` + id);
      setFaqData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setFaqData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (value) => {
    const formData = new FormData();
    formData.append("order", value?.order ? value?.order : "");
    formData.append("question", value?.question ? value?.question : "");
    formData.append("answer", value?.answer ? value?.answer : "");
    formData.append("type", value?.type ? value?.type : "");
    formData.append("module_id", value?.module_id ? value?.module_id : "");

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.GET_ADMIN_FAQ_LIST}${edit ? `/${id}` : ""}`,
        formData,
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditFaq();
        fetchFaqData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        order: faqData?.order,
        question: faqData?.question,
        answer: faqData?.answer ? faqData?.answer : "",
        type: faqData?.type ? faqData?.type : "",
        module_id: faqData?.module_id ? faqData?.module_id : "",
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      // validationSchema={AddAdminFaqSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin fullscreen={true} />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg grid gap-4">
              <div className="grid gap-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="order">
                      <CustomLabelText
                        required={true}
                        labelText="Order"
                        errorField={errors?.order}
                      />
                    </Label>

                    <Input
                      id="order"
                      type="number"
                      name="order"
                      min={1} // 🚫 no 0 or negatives
                      step={1}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={values?.order || ""}
                      onKeyDown={(e) => {
                        // block dot, minus, exponent
                        if ([".", "-", "e", "E"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;

                        // allow empty while typing
                        if (value === "") {
                          handleChange(e);
                          return;
                        }

                        // only whole numbers greater than 0
                        if (/^[1-9]\d*$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      onBlur={handleBlur}
                      autoComplete="off"
                      className={
                        errors?.order
                          ? "border-red-500 focus-visible:ring-red-500"
                          : ""
                      }
                    />
                  </div>
                
                  <div className="grid gap-2">
                    <div className="min-w-0">
                      <Label htmlFor="cdd">
                        <CustomLabelText
                          labelText="Type"
                          errorField={errors?.type}
                        />
                      </Label>
                      <div className="relative mt-2">
                        <SearchSelect
                          frameworks={faqTypeOptions}
                          optionType={"Type"}
                          handleSelect={(e) => {
                            setFieldValue("type", e);
                          }}
                          handleBlur={handleBlur}
                          className={
                            errors?.type &&
                            `focus-visible:ring-red-500 border-red-500`
                          }
                          initialValue={values?.type}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="module_id">
                      <CustomLabelText
                        required={true}
                        labelText="Module"
                        errorField={errors?.module_id}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={module}
                        optionType={"Module"}
                        handleSelect={(e) => {
                          setFieldValue("module_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.type &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.module_id}
                        disabled={false}
                      />
                      {allModuleLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="grid gap-2">
                    <Label htmlFor="question">
                      <CustomLabelText
                        required={true}
                        labelText="Question"
                        errorField={errors?.question}
                      />
                    </Label>
                    <Input
                      id="question"
                      type="text"
                      name="question"
                      value={values?.question ? values?.question : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.question &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                <div className="grid gap-2">
                  <Label htmlFor="answer">
                    <CustomLabelText
                      labelText="Answer"
                      errorField={errors?.answer}
                    />
                  </Label>
                  <Editor
                    value={values?.answer}
                    setValue={(data) => setFieldValue("answer", data)}
                    image={true}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditFaq();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditFaq;
